import type { BaseLinkRoute, BaseRoute } from '@connected/core';

import { isGroupRoute, isLinkRoute, isRouteActive } from '@connected/core';
import { Link, useLocation, useParams, useSearch } from '@tanstack/react-router';
import { Fragment } from 'react';

import { Separator } from '../../../components/separator';
import {
    SidebarGroup,
    SidebarGroupContent,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
} from '../../../components/sidebar';
import { useSidebar } from '../../../hooks/use-sidebar';
import { cn } from '../../../lib/utils';

// ============================================================================
// Types
// ============================================================================

export type SidebarNavigationProps<TContext> = {
    /**
     * Hook that returns the sidebar routes.
     */
    useSidebarRoutes: () => BaseRoute<TContext>[];
    /**
     * Function to render the label for a route.
     * Typically used for i18n translation.
     */
    renderLabel: (route: BaseLinkRoute<TContext>) => string;
};

// ============================================================================
// Component
// ============================================================================

/**
 * Generic sidebar navigation component that renders navigation groups.
 * Works with any app's navigation system.
 *
 * @example
 * // In your app:
 * import { SidebarNavigation } from '@connected/ui/features/navigation';
 * import { useSidebarRoutes } from './hooks/use-navigation';
 *
 * function MySidebarNavigation() {
 *     const { t } = useTranslation('navigation');
 *     return (
 *         <SidebarNavigation
 *             useSidebarRoutes={useSidebarRoutes}
 *             renderLabel={(route) => t(route.name)}
 *         />
 *     );
 * }
 */
export function SidebarNavigation<TContext>({
    useSidebarRoutes,
    renderLabel,
}: SidebarNavigationProps<TContext>) {
    const filteredRoutes = useSidebarRoutes();

    // Extract groups for rendering with separators
    const groups: BaseRoute<TContext>[][] = [];
    let currentGroup: BaseRoute<TContext>[] = [];

    for (const route of filteredRoutes) {
        if (isGroupRoute(route)) {
            // Start a new group if current one has items
            if (currentGroup.length > 0) {
                groups.push(currentGroup);
                currentGroup = [];
            }
            // Add all link routes from this group
            for (const childRoute of route.routes) {
                if (isLinkRoute(childRoute)) {
                    currentGroup.push(childRoute);
                }
            }
            // Finish the group
            if (currentGroup.length > 0) {
                groups.push(currentGroup);
                currentGroup = [];
            }
        }
        else if (isLinkRoute(route)) {
            currentGroup.push(route);
        }
    }

    // Push any remaining routes
    if (currentGroup.length > 0) {
        groups.push(currentGroup);
    }

    return (
        <SidebarGroup>
            <SidebarGroupContent>
                <SidebarMenu className="flex gap-2 px-4">
                    {groups.map((group, index) => (
                        <Fragment key={`group-${index}`}>
                            {group.map(route => (
                                isLinkRoute(route) && (
                                    <SidebarNavigationItem
                                        key={route.path}
                                        route={route}
                                        label={renderLabel(route)}
                                    />
                                )
                            ))}
                            {index < groups.length - 1 && <Separator />}
                        </Fragment>
                    ))}
                </SidebarMenu>
            </SidebarGroupContent>
        </SidebarGroup>
    );
}

// ============================================================================
// Menu Item Component
// ============================================================================

type SidebarNavigationItemProps<TContext> = {
    route: BaseLinkRoute<TContext>;
    label: string;
};

function SidebarNavigationItem<TContext>({
    route,
    label,
}: SidebarNavigationItemProps<TContext>) {
    const { toggleSidebar, isMobile } = useSidebar();
    const params = useParams({ strict: false });
    const search = useSearch({ strict: false });
    const location = useLocation();

    // Custom active check: matches route or any of its children
    const isActive = isRouteActive(route, location.pathname, params);

    return (
        <SidebarMenuItem>
            <SidebarMenuButton asChild>
                <Link
                    to={route.path}
                    className={cn(
                        'transition duration-200',
                        isActive
                            ? 'font-semibold text-primary bg-background-tertiary hover:!text-primary'
                            : 'text-text-tertiary hover:text-text-secondary hover:bg-background-tertiary',
                    )}
                    onClick={() => isMobile ? toggleSidebar() : undefined}
                    search={search}
                    params={params}
                >
                    {route.icon && <route.icon />}
                    <span>{label}</span>
                </Link>
            </SidebarMenuButton>
        </SidebarMenuItem>
    );
}
